// Takeoff Canvas 2.0 Track K — revision compare (roadmap Track K: "build on
// the existing revision model and sp_CreateRevision; compare existing
// canonical records: rooms, drawn conditions / openings, RoomElements,
// engineering rows, estimate lines"). Pure functions over the rows the
// existing read procs already return for two revisions — nothing is stored,
// no snapshot table, no new identity column.
//
// Identity across revisions. sp_CreateRevision (db/90) copies every record
// with NEW ids, so rows are matched by what it carries unchanged:
//   rooms        RoomNumber (the estimator-facing number — CLAUDE.md: "the
//                intended source of truth for aligning the takeoff") when both
//                sides have one, else the room name
//   conditions   Mark (openings) → AutoKey (generated symbols, re-keyed by
//                room) → AutoWallKey (wall runs, room ids re-keyed) → else
//                type code + room + rounded centroid
//   elements     room key + the catalog entity (Kind + ItemID / AssemblyID)
//   engineering  room key
//   estimate     Kind + entity id + description
// A record that matches nowhere is "added" (in the newer revision only) or
// "removed" (in the older only); a matched pair with material differences is
// "changed", each difference named field by field.

import { polygonArea, polygonPerimeter } from "./takeoffGeometry";
import { entityKeyOf } from "../config/roomElements";

const num = (v) => (v == null || v === "" || !Number.isFinite(+v) ? null : +v);
const near = (a, b, tol = 0.05) => (a == null && b == null) || (a != null && b != null && Math.abs(a - b) <= tol);
const r1 = (v) => (v == null ? null : Math.round(v * 10) / 10);
const r2 = (v) => (v == null ? null : Math.round(v * 100) / 100);
const parseJson = (s) => { try { return s ? JSON.parse(s) : null; } catch { return null; } };

// ── rooms ────────────────────────────────────────────────────────────────────
export function roomKeyOf(room) {
  const n = String(room?.RoomNumber ?? "").trim();
  if (n) return `num:${n.toLowerCase()}`;
  return `name:${String(room?.RoomName || "").trim().toLowerCase()}`;
}
export const roomLabel = (r) => r ? `${r.RoomNumber ? `${r.RoomNumber} · ` : ""}${r.RoomName || `Room ${r.RoomID}`}` : "";

export function roomFloorAreaSf(room) {
  const poly = parseJson(room?.PolygonJson)?.points;
  if (Array.isArray(poly) && poly.length >= 3) return polygonArea(poly);
  const L = num(room?.LengthFt), W = num(room?.WidthFt);
  return L != null && W != null ? L * W : null;
}
const cornerCount = (room) => { const p = parseJson(room?.PolygonJson)?.points; return Array.isArray(p) && p.length >= 3 ? p.length : null; };

// Pair rooms of two revisions: RoomNumber first, then name among the unpaired.
export function matchRooms(fromRooms = [], toRooms = []) {
  const pairs = [], removed = [];
  const unpairedTo = new Set(toRooms.map(r => r.RoomID));
  const byNum = new Map(), byName = new Map();
  for (const r of toRooms) {
    const n = String(r.RoomNumber ?? "").trim().toLowerCase(); if (n && !byNum.has(n)) byNum.set(n, r);
    const nm = String(r.RoomName || "").trim().toLowerCase(); if (nm) { if (!byName.has(nm)) byName.set(nm, []); byName.get(nm).push(r); }
  }
  for (const a of fromRooms) {
    const n = String(a.RoomNumber ?? "").trim().toLowerCase();
    let b = n ? byNum.get(n) : null;
    if (b && !unpairedTo.has(b.RoomID)) b = null;
    if (!b) b = (byName.get(String(a.RoomName || "").trim().toLowerCase()) || []).find(x => unpairedTo.has(x.RoomID)) || null;
    if (b) { unpairedTo.delete(b.RoomID); pairs.push({ from: a, to: b }); } else removed.push(a);
  }
  const added = toRooms.filter(r => unpairedTo.has(r.RoomID));
  return { pairs, added, removed };
}

function roomChanges(a, b) {
  const ch = [];
  if (String(a.RoomName || "") !== String(b.RoomName || "")) ch.push({ field: "name", from: a.RoomName, to: b.RoomName });
  if (String(a.RoomNumber ?? "") !== String(b.RoomNumber ?? "")) ch.push({ field: "number", from: a.RoomNumber ?? null, to: b.RoomNumber ?? null });
  for (const [f, label] of [["LengthFt", "length"], ["WidthFt", "width"], ["HeightFt", "height"]]) if (!near(num(a[f]), num(b[f]))) ch.push({ field: label, from: r2(num(a[f])), to: r2(num(b[f])), unit: "ft" });
  const areaA = roomFloorAreaSf(a), areaB = roomFloorAreaSf(b);
  if (!near(areaA, areaB, 0.5)) ch.push({ field: "floor area", from: r1(areaA), to: r1(areaB), unit: "SF" });
  if (cornerCount(a) !== cornerCount(b)) ch.push({ field: "outline", from: cornerCount(a) ? `${cornerCount(a)} corners` : "rectangle", to: cornerCount(b) ? `${cornerCount(b)} corners` : "rectangle" });
  if (!near(num(a.CanvasX), num(b.CanvasX)) || !near(num(a.CanvasY), num(b.CanvasY))) ch.push({ field: "position", from: a.CanvasX == null ? "unplaced" : `${r2(num(a.CanvasX))}, ${r2(num(a.CanvasY))}`, to: b.CanvasX == null ? "unplaced" : `${r2(num(b.CanvasX))}, ${r2(num(b.CanvasY))}` });
  if (!near(num(a.ACH), num(b.ACH), 0.01)) ch.push({ field: "ACH", from: num(a.ACH), to: num(b.ACH) });
  if ((a.RoomTemplateID ?? null) !== (b.RoomTemplateID ?? null)) ch.push({ field: "template", from: a.RoomTemplateID ?? null, to: b.RoomTemplateID ?? null });
  if ((a.ClassificationID ?? null) !== (b.ClassificationID ?? null)) ch.push({ field: "classification", from: a.ClassificationID ?? null, to: b.ClassificationID ?? null });
  if ((a.WallSystemsJson ?? null) !== (b.WallSystemsJson ?? null)) ch.push({ field: "wall systems by position", from: a.WallSystemsJson ? "set" : "none", to: b.WallSystemsJson ? "set" : "none" });
  if ((a.WallKind ?? null) !== (b.WallKind ?? null) || (a.WallItemID ?? null) !== (b.WallItemID ?? null) || (a.WallAssemblyID ?? null) !== (b.WallAssemblyID ?? null)) ch.push({ field: "wall system", from: a.WallKind ? `${a.WallKind} ${a.WallItemID ?? a.WallAssemblyID}` : "template default", to: b.WallKind ? `${b.WallKind} ${b.WallItemID ?? b.WallAssemblyID}` : "template default" });
  return ch;
}

// ── conditions ───────────────────────────────────────────────────────────────
// Measured quantity of a drawn row from its own geometry: LF for a run, SF for
// an area, 1 for a placed symbol; plus its centroid.
export function conditionMetric(row) {
  const g = parseJson(row?.GeometryJson);
  const pts = Array.isArray(g?.points) ? g.points.filter(p => Number.isFinite(+p?.x) && Number.isFinite(+p?.y)) : null;
  if (pts && pts.length >= 2) {
    const cx = pts.reduce((s, p) => s + +p.x, 0) / pts.length, cy = pts.reduce((s, p) => s + +p.y, 0) / pts.length;
    if (row.Style === "area" && pts.length >= 3) return { kind: "SF", value: polygonArea(pts), centroid: { x: cx, y: cy } };
    let len = 0; for (let i = 1; i < pts.length; i++) len += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
    if (row.Style === "area") len = polygonPerimeter(pts);
    return { kind: "LF", value: len, centroid: { x: cx, y: cy } };
  }
  if (g && Number.isFinite(+g.x) && Number.isFinite(+g.y)) return { kind: "EA", value: 1, centroid: { x: +g.x, y: +g.y } };
  return { kind: row?.Style === "count" ? "EA" : row?.Style === "area" ? "SF" : "LF", value: row?.Style === "count" ? 1 : 0, centroid: null };
}

function remapWallKey(key, roomKeyById) {
  const m = String(key || "").match(/^(shared|cavity):(\d+):(\d+):(.*)$/);
  if (m) { const ka = roomKeyById.get(+m[2]) || `id${m[2]}`, kb = roomKeyById.get(+m[3]) || `id${m[3]}`; const [x, y] = m[1] === "shared" ? [ka, kb].sort() : [ka, kb]; return `${m[1]}:${x}:${y}:${m[4]}`; }
  const e = String(key || "").match(/^ext:(\d+)$/);
  if (e) return `ext:${roomKeyById.get(+e[1]) || `id${e[1]}`}`;
  return String(key || "");
}

export function conditionKeyOf(row, roomKeyById) {
  const roomKey = row.RoomID != null ? (roomKeyById.get(row.RoomID) || `id${row.RoomID}`) : "none";
  if (row.Mark) return `mark:${String(row.Mark).trim().toLowerCase()}`;
  if (row.AutoKey) { const [system, , index] = String(row.AutoKey).split(":"); return `auto:${system}:${roomKey}:${index}`; }
  if (row.AutoWallKey) return `wall:${remapWallKey(row.AutoWallKey, roomKeyById)}`;
  const m = conditionMetric(row);
  const c = m.centroid ? `${Math.round(m.centroid.x * 2) / 2},${Math.round(m.centroid.y * 2) / 2}` : "-";
  return `geom:${row.Code || row.Name || row.Kind || "?"}:${row.MeasureRole || "add"}:${roomKey}:${c}`;
}
export const conditionLabel = (row) => `${row.MeasureRole === "deduct" ? "Back-out" : row.Mark ? `${row.Mark} · ` : ""}${row.MeasureRole === "deduct" ? "" : (row.Name || row.Code || "condition")}`.trim();

function conditionChanges(a, b) {
  const ch = [];
  const ma = conditionMetric(a), mb = conditionMetric(b);
  if (ma.kind === mb.kind && ma.kind !== "EA" && !near(ma.value, mb.value, ma.kind === "SF" ? 0.5 : 0.1)) ch.push({ field: ma.kind === "SF" ? "area" : "length", from: r1(ma.value), to: r1(mb.value), unit: ma.kind });
  if (ma.centroid && mb.centroid && !near(ma.centroid.x, mb.centroid.x, 0.1) || (ma.centroid && mb.centroid && !near(ma.centroid.y, mb.centroid.y, 0.1))) ch.push({ field: "position", from: `${r1(ma.centroid.x)}, ${r1(ma.centroid.y)}`, to: `${r1(mb.centroid.x)}, ${r1(mb.centroid.y)}` });
  if ((a.Code || a.Name || null) !== (b.Code || b.Name || null)) ch.push({ field: "type", from: a.Code || a.Name, to: b.Code || b.Name });
  if (!near(num(a.QuantityOverride), num(b.QuantityOverride), 0.001)) ch.push({ field: "quantity override", from: num(a.QuantityOverride), to: num(b.QuantityOverride) });
  if (!near(num(a.HeightFtOverride), num(b.HeightFtOverride), 0.01)) ch.push({ field: "height override", from: num(a.HeightFtOverride), to: num(b.HeightFtOverride), unit: "ft" });
  if ((a.DeductionPolicy ?? null) !== (b.DeductionPolicy ?? null)) ch.push({ field: "deduction policy", from: a.DeductionPolicy ?? "inherit", to: b.DeductionPolicy ?? "inherit" });
  if (!!a.AutoPositionLocked !== !!b.AutoPositionLocked) ch.push({ field: "position locked", from: !!a.AutoPositionLocked, to: !!b.AutoPositionLocked });
  return ch;
}

// ── elements / engineering / estimate lines ──────────────────────────────────
const elementKey = (el, roomKeyById) => `${roomKeyById.get(el.RoomID) || `id${el.RoomID}`}|${entityKeyOf(el)}`;
function elementChanges(a, b) {
  const ch = [];
  if ((a.QuantityMethod || "calculated") !== (b.QuantityMethod || "calculated")) ch.push({ field: "quantity method", from: a.QuantityMethod || "calculated", to: b.QuantityMethod || "calculated" });
  if (!near(num(a.FixedQty), num(b.FixedQty), 0.001)) ch.push({ field: "fixed qty", from: num(a.FixedQty), to: num(b.FixedQty) });
  if (!near(num(a.OverrideQty), num(b.OverrideQty), 0.001)) ch.push({ field: "override", from: num(a.OverrideQty), to: num(b.OverrideQty) });
  if ((a.FormulaCode || a.QuantityFormulaID || null) !== (b.FormulaCode || b.QuantityFormulaID || null)) ch.push({ field: "formula", from: a.FormulaCode || a.QuantityFormulaID || null, to: b.FormulaCode || b.QuantityFormulaID || null });
  if ((a.Discipline || null) !== (b.Discipline || null)) ch.push({ field: "discipline", from: a.Discipline, to: b.Discipline });
  if ((a.SettingsJson || null) !== (b.SettingsJson || null)) ch.push({ field: "settings", from: a.SettingsJson || "—", to: b.SettingsJson || "—" });
  return ch;
}
function engineeringChanges(a, b) {
  const ch = [];
  for (const [f, label, tol] of [["EffectiveHEPACount", "HEPA effective", 0], ["CalculatedHEPACount", "HEPA calculated", 0], ["EffectiveLightingFixtureCount", "lights effective", 0], ["CalculatedLightingFixtureCount", "lights calculated", 0], ["SupplyCFM", "supply CFM", 0.5], ["TargetACH", "ACH", 0.01], ["FloorAreaSf", "floor area", 0.5]])
    if (!near(num(a[f]), num(b[f]), tol)) ch.push({ field: label, from: r1(num(a[f])), to: r1(num(b[f])) });
  return ch;
}
const lineKey = (l) => `${l.Kind || "manual"}:${l.ItemID ?? l.AssemblyID ?? "-"}:${String(l.Description || "").trim().toLowerCase()}`;
function lineChanges(a, b) {
  const ch = [];
  if (!near(num(a.Qty), num(b.Qty), 0.001)) ch.push({ field: "qty", from: num(a.Qty), to: num(b.Qty) });
  if ((a.CostSource || "catalog") !== (b.CostSource || "catalog")) ch.push({ field: "cost source", from: a.CostSource || "catalog", to: b.CostSource || "catalog" });
  if (!near(num(a.CarriedUnitCost), num(b.CarriedUnitCost), 0.005)) ch.push({ field: "carried unit cost", from: num(a.CarriedUnitCost), to: num(b.CarriedUnitCost), unit: "$" });
  if ((a.AlternateID ?? null) !== (b.AlternateID ?? null)) ch.push({ field: "alternate", from: a.AlternateID ?? "base scope", to: b.AlternateID ?? "base scope" });
  return ch;
}

// Generic keyed diff. keyFn(row) → identity; label(row); changes(a, b) → [].
function diffKeyed(fromRows, toRows, keyFn, label, changes) {
  const toBy = new Map();
  for (const r of toRows) { const k = keyFn(r); if (!toBy.has(k)) toBy.set(k, []); toBy.get(k).push(r); }
  const added = [], removed = [], changed = []; let unchanged = 0;
  for (const a of fromRows) {
    const k = keyFn(a); const bucket = toBy.get(k);
    if (!bucket || !bucket.length) { removed.push({ key: k, label: label(a), row: a }); continue; }
    const b = bucket.shift();
    const ch = changes(a, b);
    if (ch.length) changed.push({ key: k, label: label(b), from: a, to: b, changes: ch }); else unchanged += 1;
  }
  for (const [k, bucket] of toBy) for (const b of bucket) added.push({ key: k, label: label(b), row: b });
  return { added, removed, changed, unchanged };
}

export const describeChange = (c) => `${c.field}: ${fmtVal(c.from, c.unit)} → ${fmtVal(c.to, c.unit)}`;
const fmtVal = (v, unit) => v == null ? "—" : typeof v === "boolean" ? (v ? "yes" : "no") : `${typeof v === "number" ? v.toLocaleString() : v}${unit && typeof v === "number" ? ` ${unit}` : ""}`;

// Drawn quantity per type code (additive rows only; back-outs are listed by
// their element, never summed here).
function drawnTotals(rows) {
  const out = new Map();
  for (const r of rows) {
    if (r.MeasureRole === "deduct") continue;
    const m = conditionMetric(r);
    const code = r.Code || r.Name || "—";
    const k = `${code}|${m.kind}`;
    if (!out.has(k)) out.set(k, { code, name: r.Name || r.Code || "", kind: m.kind, value: 0, count: 0 });
    const t = out.get(k); t.value += m.kind === "EA" ? 1 : m.value; t.count += 1;
  }
  return out;
}

// from / to: { revision?, rooms, conditions, elements, engineering, lines }
export function compareRevisions({ from, to }) {
  const F = { rooms: [], conditions: [], elements: [], engineering: [], lines: [], ...(from || {}) };
  const T = { rooms: [], conditions: [], elements: [], engineering: [], lines: [], ...(to || {}) };
  const rm = matchRooms(F.rooms, T.rooms);
  const roomKeyFrom = new Map(F.rooms.map(r => [r.RoomID, roomKeyOf(r)]));
  const roomKeyTo = new Map(T.rooms.map(r => [r.RoomID, roomKeyOf(r)]));
  // A pair matched by name whose number changed must still share one key on
  // both sides — use the newer revision's key for both.
  for (const p of rm.pairs) { roomKeyFrom.set(p.from.RoomID, roomKeyOf(p.to)); roomKeyTo.set(p.to.RoomID, roomKeyOf(p.to)); }

  const rooms = {
    added: rm.added.map(r => ({ key: roomKeyOf(r), label: roomLabel(r), row: r })),
    removed: rm.removed.map(r => ({ key: roomKeyOf(r), label: roomLabel(r), row: r })),
    changed: rm.pairs.map(p => ({ key: roomKeyOf(p.to), label: roomLabel(p.to), from: p.from, to: p.to, changes: roomChanges(p.from, p.to) })).filter(x => x.changes.length),
    unchanged: 0,
  };
  rooms.unchanged = rm.pairs.length - rooms.changed.length;

  // Each side is keyed with ITS OWN room map (ids differ per revision; the keys agree).
  const conditionsTo = diffKeyed(F.conditions.map(r => ({ ...r, __k: conditionKeyOf(r, roomKeyFrom) })), T.conditions.map(r => ({ ...r, __k: conditionKeyOf(r, roomKeyTo) })), r => r.__k, conditionLabel, conditionChanges);
  const elements = diffKeyed(F.elements.map(e => ({ ...e, __k: elementKey(e, roomKeyFrom) })), T.elements.map(e => ({ ...e, __k: elementKey(e, roomKeyTo) })), e => e.__k, e => `${e.Name || e.Code || "element"}`, elementChanges);
  const engineering = diffKeyed(F.engineering.map(e => ({ ...e, __k: roomKeyFrom.get(e.RoomID) || `id${e.RoomID}` })), T.engineering.map(e => ({ ...e, __k: roomKeyTo.get(e.RoomID) || `id${e.RoomID}` })), e => e.__k, e => e.__k, engineeringChanges);
  const lines = diffKeyed(F.lines, T.lines, lineKey, l => l.Description || `${l.Kind} ${l.ItemID ?? l.AssemblyID ?? ""}`, lineChanges);

  // Room labels for element / engineering rows (the key is the room key).
  const roomByKey = new Map(T.rooms.map(r => [roomKeyOf(r), r]));
  for (const r of F.rooms) if (!roomByKey.has(roomKeyFrom.get(r.RoomID))) roomByKey.set(roomKeyFrom.get(r.RoomID), r);
  const withRoom = (sec) => { for (const list of [sec.added, sec.removed, sec.changed]) for (const it of list) { const rk = String(it.key).split("|")[0]; it.room = roomLabel(roomByKey.get(rk)) || rk; } return sec; };
  withRoom(elements); withRoom(engineering);

  // Quantity impact — totals both sides.
  const sumArea = (rows) => rows.reduce((s, r) => s + (roomFloorAreaSf(r) || 0), 0);
  const engSum = (rows, f) => rows.reduce((s, r) => s + (num(r[f]) || 0), 0);
  const dtF = drawnTotals(F.conditions), dtT = drawnTotals(T.conditions);
  const byCode = [];
  for (const k of new Set([...dtF.keys(), ...dtT.keys()])) {
    const a = dtF.get(k), b = dtT.get(k);
    const from = a ? a.value : 0, toV = b ? b.value : 0;
    if (!near(from, toV, 0.05)) byCode.push({ code: (b || a).code, name: (b || a).name, kind: (b || a).kind, from: r1(from), to: r1(toV), delta: r1(toV - from) });
  }
  byCode.sort((x, y) => Math.abs(y.delta) - Math.abs(x.delta));
  const summary = {
    rooms: { from: F.rooms.length, to: T.rooms.length },
    floorSf: { from: r1(sumArea(F.rooms)), to: r1(sumArea(T.rooms)) },
    hepa: { from: engSum(F.engineering, "EffectiveHEPACount"), to: engSum(T.engineering, "EffectiveHEPACount") },
    lights: { from: engSum(F.engineering, "EffectiveLightingFixtureCount"), to: engSum(T.engineering, "EffectiveLightingFixtureCount") },
    supplyCfm: { from: r1(engSum(F.engineering, "SupplyCFM")), to: r1(engSum(T.engineering, "SupplyCFM")) },
    byCode,
    counts: {
      rooms: rooms.added.length + rooms.removed.length + rooms.changed.length,
      conditions: conditionsTo.added.length + conditionsTo.removed.length + conditionsTo.changed.length,
      elements: elements.added.length + elements.removed.length + elements.changed.length,
      engineering: engineering.added.length + engineering.removed.length + engineering.changed.length,
      lines: lines.added.length + lines.removed.length + lines.changed.length,
    },
  };
  summary.total = Object.values(summary.counts).reduce((s, n) => s + n, 0);
  return { rooms, conditions: conditionsTo, elements, engineering, lines, summary };
}

// The revision an estimator most likely wants to compare against: the
// previous revision by number, else the newest other one.
export function defaultCompareRevision(revisions = [], activeRevId) {
  const active = revisions.find(r => r.RevisionID === activeRevId);
  const others = revisions.filter(r => r.RevisionID !== activeRevId);
  if (!others.length) return null;
  const lower = others.filter(r => active && +r.RevisionNumber < +active.RevisionNumber).sort((a, b) => +b.RevisionNumber - +a.RevisionNumber);
  return (lower[0] || others.sort((a, b) => +b.RevisionNumber - +a.RevisionNumber)[0]) || null;
}
